import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'

import * as formEn from '@/core/form/locales/en'
import * as formJa from '@/core/form/locales/ja'
import * as en from '@/core/i18n/locales/en'
import * as ja from '@/core/i18n/locales/ja'

import commonEn from '../../../apps/current/locales/en/common.json'
import commonJa from '../../../apps/current/locales/ja/common.json'

export async function init() {
  i18n.use(initReactI18next).init({
    lng: 'ja',
    fallbackLng: 'ja',
    resources: {
      ja: { ...ja, ...formJa },
      en: { ...en, ...formEn },
    },
    returnObjects: true,
    defaultNS: 'common',
    fallbackNS: 'common',
    interpolation: {
      format(value, format) {
        if (format === 'uppercase') return value?.toUpperCase()
        if (format === 'lowercase') return value?.toLowerCase()
        return value
      },
      escapeValue: false,
      skipOnVariables: false,
    },
  })

  /**
   * 各アプリのcommon.jsonをパースしてi18nの値を上書きする
   */
  const i18nResourcesJa = { common: {} }
  const i18nResourcesEn = { common: {} }
  Object.entries(commonJa).forEach(([key, value]) => {
    if (typeof value === 'object' && value !== null) {
      // namespacesはネームスペースカスタム用のオブジェクトとして含める
      if (key === 'namespaces') {
        i18nResourcesJa['common'] = {
          ...i18nResourcesJa['common'],
          [key]: value,
        }
      } else {
        i18n.addResourceBundle('ja', key, value, true, true)
      }
    } else {
      i18nResourcesJa['common'] = {
        ...i18nResourcesJa['common'],
        [key]: value,
      }
    }
  })
  Object.entries(commonEn).forEach(([key, value]) => {
    if (typeof value === 'object' && value !== null) {
      // namespacesはネームスペースカスタム用のオブジェクトとして含める
      if (key === 'namespaces') {
        i18nResourcesEn['common'] = {
          ...i18nResourcesEn['common'],
          [key]: value,
        }
      } else {
        i18n.addResourceBundle('en', key, value, true, true)
      }
    } else {
      i18nResourcesEn['common'] = {
        ...i18nResourcesEn['common'],
        [key]: value,
      }
    }
  })
  i18n.addResourceBundle('ja', 'common', i18nResourcesJa['common'], true, true)
  i18n.addResourceBundle('en', 'common', i18nResourcesEn['common'], true, true)

  // common.jsonのオブジェクト型のキーを各ネームスペースに展開
  expandCommonNamespaces('ja')
  expandCommonNamespaces('en')

  /**
   * デフォルト言語を日本語に設定
   */
  i18n.changeLanguage('ja')
}

/**
 * common.jsonのネームスペース上書き用のキーを各ネームスペースに展開する
 *
 * [使い方]
 * アプリ内で使用する文言をアプリ固有のものに上書きしたい場合に使用する
 * アプリのcommon.jsonに以下のようにネームスペース上書き用のキーをオブジェクト型で追加する
 *
 * @example
 * // common.json
 * {
 *   "metaTitle": "デジリアトレカ",
 *   "namespaces": {
 *     "artistTop": {
 *       "title": "デジリア専用トップタイトル",
 *       "newText": "デジリアへようこそ"
 *     }
 *   }
 * }
 *
 * - ファイル: apps/"アプリ名"/locales/ja/common.json,  apps/"アプリ名"/locales/en/common.json
 * - namespacesキーの下にネームスペース上書き用のオブジェクトを配置
 * - ネームスペース名 = 対象のネームスペース名
 * - ネームスペース内のキー = ネームスペース内で使用する翻訳キー
 * - namespacesキー以外の文字列型キーは通常のcommon翻訳として扱われる
 *
 * [結果]
 *  artistTopネームスペースに以下が追加される:
 *  - title: "デジリア専用トップタイトル"
 *  - newText: "デジリアへようこそ"
 *
 *  artistCollectionsネームスペースに以下が追加される:
 *  - title: "デジリア専用コレクションタイトル"
 *
 * 各ネームスペースの既存定義は上書きされ、存在しない定義は追加される。
 * それ以外の定義はそのまま残る
 *
 * [使用例]
 *  useTranslation('artistTop')でt('title')を使うと、"デジリア専用トップタイトル"が返される
 */
const expandCommonNamespaces = (lang: string) => {
  const commonData = i18n.getResourceBundle(lang, 'common')
  // common.jsonのnamespacesキー内のオブジェクトを各ネームスペースに展開
  if (commonData.namespaces && typeof commonData.namespaces === 'object') {
    Object.entries(commonData.namespaces).forEach(
      ([namespace, translations]) => {
        if (
          typeof translations === 'object' &&
          translations !== null &&
          !Array.isArray(translations)
        ) {
          const existingData = i18n.getResourceBundle(lang, namespace) || {}
          const mergedData = { ...existingData, ...translations }
          i18n.addResourceBundle(lang, namespace, mergedData, true, true)
        }
      }
    )
  }
}

export { i18n }
